import React, { ComponentProps, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from '../link/link';
import { Typography } from '../typography/typography';
import { Icon } from '../icon/icon';
import TriangleShape from '../shape/assets/triangle-shape.svg?react';
import { notNil } from '../../core/helpers/is-nil';
import { AccentBgProvider, useAccentBgContext } from '../accent-bg-context/accent-bg-context';
import styles from './tooltip.module.scss';

type Position = 'topMiddle' | 'topLeft' | 'topRight' | 'bottomMiddle' | 'bottomLeft' | 'bottomRight';

type Props = {
  title?: string;
  text?: string;
  link?: Pick<ComponentProps<typeof Link>, 'to' | 'children'>;
  defaultOpen?: boolean;
  position?: Position;
  tabIndex?: number;
};

const positionPropsMap: Record<
  Position,
  {
    side?: 'left' | 'right' | 'bottom' | 'top';
    align?: 'start' | 'end' | 'center';
  }
> = {
  topMiddle: { side: 'top', align: 'center' },
  topLeft: { side: 'top', align: 'start' },
  topRight: { side: 'top', align: 'end' },
  bottomMiddle: { side: 'bottom', align: 'center' },
  bottomLeft: { side: 'bottom', align: 'start' },
  bottomRight: { side: 'bottom', align: 'end' },
};

const CONTENT_OFFSET = -6;

export const Tooltip: React.FC<Props> = ({
  title,
  text,
  link,
  defaultOpen = false,
  position = 'topMiddle',
  tabIndex,
}) => {
  const [isOpen, setOpen] = useState<boolean>(defaultOpen);
  const positionProps = positionPropsMap[position];
  const hasText = notNil(text);
  const hasLink = notNil(link);
  const hasTextContainerContent = hasText || hasLink;

  const { isAccentBackground } = useAccentBgContext();
  const { t } = useTranslation();

  function open() {
    setOpen(true);
  }

  function close() {
    setOpen(false);
  }

  return (
    <Popover.Root open={isOpen}>
      <Popover.Trigger asChild onMouseEnter={open} onMouseLeave={close}>
        <button
          aria-label={t('a11y.tooltip.button')}
          className={classNames(styles.trigger, { [styles.onAccentBg]: isAccentBackground })}
          tabIndex={tabIndex}
        >
          <Icon className={classNames({ [styles.isOpen]: isOpen })} size="16" variant="circle-i" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={styles.content}
          sideOffset={CONTENT_OFFSET}
          onMouseEnter={open}
          onMouseLeave={close}
          {...positionProps}
        >
          <AccentBgProvider>
            {notNil(title) ? (
              <Typography fontStyle="bold" variant="smalltext">
                {title}
              </Typography>
            ) : null}
            {hasTextContainerContent ? (
              <Typography className={styles.textContainer} variant="smalltext">
                {hasText ? <span className={styles.text}>{text}</span> : null}
                {hasLink ? <Link {...link} variant="smalltext" /> : null}
              </Typography>
            ) : null}
            <Popover.Arrow asChild>
              <div className={styles.arrowContainer}>
                <TriangleShape />
              </div>
            </Popover.Arrow>
          </AccentBgProvider>
          {hasLink ? <div className={styles.areaExpander} /> : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
